import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  ArrowLeft, Trash, PencilSimple, EnvelopeSimple, Phone, ChatCircleText, Globe,
  FilePdf, Buildings, ArrowRight, Plus, UploadSimple, MapPin, Browsers, PaperPlaneRight, NotePencil,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtTs = (iso) => { try { return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

export default function PropertyManagerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [channel, setChannel] = useState("sms");
  const [sms, setSms] = useState("");
  const [emailSubj, setEmailSubj] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [note, setNote] = useState("");
  const contractRef = useRef();

  const load = () => api.get(`/property-managers/${id}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const pm = data.manager;
  const channels = data.channels || {};
  const smsMsgs = data.messages.filter((m) => m.channel === "sms");
  const emailMsgs = data.messages.filter((m) => m.channel === "email");

  const openEdit = () => { setForm({ ...pm, management_fee_pct: pm.management_fee_pct ?? "", additional_contacts: pm.additional_contacts || [] }); setEditOpen(true); };

  const saveEdit = async () => {
    try {
      await api.put(`/property-managers/${id}`, { ...form, management_fee_pct: form.management_fee_pct === "" || form.management_fee_pct == null ? null : Number(form.management_fee_pct) });
      toast.success("Saved");
      setEditOpen(false); load();
    } catch (e) { toast.error("Save failed"); }
  };

  const removePM = async () => {
    if (!window.confirm("Delete this property manager? Properties they manage will become self-managed.")) return;
    await api.delete(`/property-managers/${id}`); toast.success("Deleted"); navigate("/property-managers");
  };

  const uploadContract = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    await api.post(`/property-managers/${id}/contract`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success("Contract uploaded"); load();
    if (contractRef.current) contractRef.current.value = "";
  };
  const openContract = async () => {
    try {
      const r = await api.get(`/property-managers/${id}/contract`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("No contract on file"); }
  };

  const sendSms = async () => {
    if (!sms.trim()) return;
    try {
      const r = await api.post(`/property-managers/${id}/sms`, { body: sms });
      setSms(""); load();
      toast[r.data.status === "sent" ? "success" : "message"](r.data.status === "sent" ? "Text sent" : r.data.status === "stubbed" ? "Logged (Twilio not connected yet)" : "Send failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const sendEmail = async () => {
    if (!emailSubj.trim() || !emailBody.trim()) { toast.error("Subject and message required"); return; }
    try {
      const r = await api.post(`/property-managers/${id}/email`, { subject: emailSubj, body: emailBody });
      setEmailSubj(""); setEmailBody(""); load();
      toast[r.data.status === "sent" ? "success" : "message"](r.data.status === "sent" ? "Email sent" : r.data.status === "stubbed" ? "Logged (Resend not connected)" : "Send failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/property-managers/${id}/notes`, { text: note }); setNote(""); load();
  };
  const delNote = async (nid) => { await api.delete(`/property-managers/${id}/notes/${nid}`); load(); };

  const ContactLink = ({ icon: Icon, label, value, href, testid }) => value ? (
    <a data-testid={testid} href={href} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
      className="flex items-center gap-3 p-3 border border-border rounded-sm hover:bg-secondary transition-colors group">
      <Icon size={18} className="shrink-0" />
      <div className="min-w-0"><p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{label}</p>
        <p className="text-sm font-semibold truncate">{value}</p></div>
      <ArrowRight size={15} className="ml-auto text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
    </a>
  ) : null;

  return (
    <div className="space-y-6 animate-fade-up">
      <button data-testid="back-to-pms" onClick={() => navigate("/property-managers")} className="text-sm text-muted-foreground flex items-center gap-1 hover:text-foreground">
        <ArrowLeft size={16} /> Property Managers
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 bg-foreground text-background flex items-center justify-center rounded-sm font-display text-2xl font-black">{pm.name?.[0]?.toUpperCase()}</div>
          <div>
            <h1 className="font-display text-3xl font-black tracking-tighter">{pm.name}</h1>
            <p className="text-sm text-muted-foreground">{pm.contact_name}{pm.states_covered ? ` · ${pm.states_covered}` : ""}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="edit-pm-button" variant="outline" onClick={openEdit} className="rounded-sm"><PencilSimple size={16} className="mr-1" /> Edit</Button>
          <Button data-testid="delete-pm-button" variant="outline" onClick={removePM} className="rounded-sm text-destructive border-destructive/30 hover:bg-destructive hover:text-destructive-foreground"><Trash size={16} /></Button>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="rounded-sm">
          <TabsTrigger data-testid="tab-overview" value="overview" className="rounded-sm">Overview</TabsTrigger>
          <TabsTrigger data-testid="tab-comms" value="comms" className="rounded-sm">Communications</TabsTrigger>
          <TabsTrigger data-testid="tab-properties" value="properties" className="rounded-sm">Properties</TabsTrigger>
          <TabsTrigger data-testid="tab-notes" value="notes" className="rounded-sm">Notes</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ContactLink testid="pm-link-email" icon={EnvelopeSimple} label="Email" value={pm.email} href={`mailto:${pm.email}`} />
            <ContactLink testid="pm-link-phone" icon={Phone} label="Call" value={pm.phone} href={`tel:${pm.phone}`} />
            <ContactLink testid="pm-link-sms" icon={ChatCircleText} label="Text" value={pm.sms_phone} href={`sms:${pm.sms_phone}`} />
            <ContactLink testid="pm-link-website" icon={Globe} label="Website" value={pm.website} href={pm.website?.startsWith("http") ? pm.website : `https://${pm.website}`} />
            <ContactLink testid="pm-link-software" icon={Browsers} label="Management Software (reports)" value={pm.software_url} href={pm.software_url?.startsWith("http") ? pm.software_url : `https://${pm.software_url}`} />
            {pm.mailing_address && (
              <div className="flex items-center gap-3 p-3 border border-border rounded-sm">
                <MapPin size={18} className="shrink-0" />
                <div className="min-w-0"><p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Mailing Address</p>
                  <p className="text-sm font-semibold">{pm.mailing_address}</p></div>
              </div>
            )}
          </div>

          {/* Contract Terms - inline editor */}
          <ContractTermsPanel pm={pm} pmId={id} onSaved={load} onUpload={uploadContract} onOpenContract={openContract} contractRef={contractRef} />

          {/* Additional contacts */}
          {(pm.additional_contacts || []).length > 0 && (
            <div className="bg-card border border-border rounded-sm p-5">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3">Additional Contacts</p>
              <div className="space-y-2">
                {pm.additional_contacts.map((c, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b border-border last:border-0 pb-2 last:pb-0">
                    <div><span className="font-semibold">{c.name}</span> <span className="text-muted-foreground">· {c.role}</span></div>
                    <div className="text-muted-foreground text-xs">{c.email} {c.phone}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* COMMUNICATIONS */}
        <TabsContent value="comms" className="mt-4">
          <div className="flex gap-2 mb-4">
            <Button data-testid="comm-sms-toggle" size="sm" variant={channel === "sms" ? "default" : "outline"} onClick={() => setChannel("sms")} className="rounded-sm"><ChatCircleText size={15} className="mr-1" /> Text {channels.sms ? "" : "(stub)"}</Button>
            <Button data-testid="comm-email-toggle" size="sm" variant={channel === "email" ? "default" : "outline"} onClick={() => setChannel("email")} className="rounded-sm"><EnvelopeSimple size={15} className="mr-1" /> Email {channels.email ? "" : "(stub)"}</Button>
          </div>

          {channel === "sms" ? (
            <div className="bg-card border border-border rounded-sm flex flex-col h-[520px]">
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {smsMsgs.length === 0 ? <p className="text-sm text-muted-foreground text-center mt-8">No texts yet. Start the conversation below.</p> :
                  smsMsgs.map((m) => (
                    <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-sm px-3 py-2 ${m.direction === "outbound" ? "bg-foreground text-background" : "bg-secondary"}`}>
                        <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                        <p className={`text-[10px] mt-1 ${m.direction === "outbound" ? "text-background/60" : "text-muted-foreground"}`}>{fmtTs(m.created_at)}{m.status === "stubbed" ? " · not sent (stub)" : m.status === "failed" ? " · failed" : ""}</p>
                      </div>
                    </div>
                  ))}
              </div>
              <div className="border-t border-border p-3 flex gap-2">
                <Input data-testid="sms-input" value={sms} onChange={(e) => setSms(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendSms()} placeholder={pm.sms_phone || pm.phone ? `Text ${pm.sms_phone || pm.phone}` : "No phone on file"} className="rounded-sm" />
                <Button data-testid="send-sms-button" onClick={sendSms} className="rounded-sm"><PaperPlaneRight size={16} /></Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-card border border-border rounded-sm p-4 space-y-3">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Compose Email {!channels.email && "· Resend not connected (will log only)"}</p>
                <Input data-testid="email-subject-input" value={emailSubj} onChange={(e) => setEmailSubj(e.target.value)} placeholder="Subject" className="rounded-sm" />
                <Textarea data-testid="email-body-input" value={emailBody} onChange={(e) => setEmailBody(e.target.value)} placeholder={`Message to ${pm.email || "(no email on file)"}`} className="rounded-sm min-h-[120px]" />
                <Button data-testid="send-email-button" onClick={sendEmail} className="rounded-sm font-semibold"><PaperPlaneRight size={15} className="mr-1" /> Send Email</Button>
              </div>
              <div className="space-y-2">
                {emailMsgs.map((m) => (
                  <div key={m.id} data-testid={`email-msg-${m.id}`} className="bg-card border border-border rounded-sm p-4">
                    <div className="flex justify-between"><p className="font-semibold text-sm">{m.subject}</p><span className="text-xs text-muted-foreground">{fmtTs(m.created_at)}</span></div>
                    <p className="text-xs text-muted-foreground">To {m.to}{m.status === "stubbed" ? " · logged (not sent)" : m.status === "failed" ? " · failed" : " · sent"}</p>
                    <p className="text-sm mt-2 whitespace-pre-wrap">{m.body}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* PROPERTIES */}
        <TabsContent value="properties" className="mt-4">
          {data.properties.length === 0 ? (
            <div className="bg-card border border-border rounded-sm p-8 text-center text-sm text-muted-foreground">
              No properties assigned. Assign this manager from a property's detail page.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.properties.map((p) => (
                <button key={p.id} data-testid={`pm-property-${p.id}`} onClick={() => navigate(`/properties/${p.id}`)}
                  className="bg-card border border-border rounded-sm p-4 text-left hover:shadow-md transition-all flex items-center gap-3 group">
                  <Buildings size={22} weight="fill" className="shrink-0" />
                  <div className="min-w-0 flex-1"><p className="font-semibold truncate">{p.name}</p><p className="text-xs text-muted-foreground">{[p.city, p.state].filter(Boolean).join(", ")}</p></div>
                  <ArrowRight size={16} className="text-muted-foreground group-hover:translate-x-1 transition-transform" />
                </button>
              ))}
            </div>
          )}
        </TabsContent>

        {/* NOTES */}
        <TabsContent value="notes" className="mt-4 space-y-4">
          <div className="bg-card border border-border rounded-sm p-4 space-y-2">
            <Textarea data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Jot a timestamped note about this manager…" className="rounded-sm min-h-[80px]" />
            <Button data-testid="add-note-button" onClick={addNote} className="rounded-sm font-semibold"><NotePencil size={15} className="mr-1" /> Add Note</Button>
          </div>
          <div className="space-y-2">
            {data.notes.length === 0 ? <p className="text-sm text-muted-foreground">No notes yet.</p> :
              data.notes.map((n) => (
                <div key={n.id} data-testid={`note-${n.id}`} className="bg-card border border-border rounded-sm p-4 group">
                  <div className="flex justify-between items-start">
                    <p className="text-sm whitespace-pre-wrap flex-1">{n.text}</p>
                    <button data-testid={`del-note-${n.id}`} onClick={() => delNote(n.id)} className="text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity ml-2"><Trash size={14} /></button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">{n.author_name} · {fmtTs(n.created_at)}</p>
                </div>
              ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit dialog */}
      {form && (
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="rounded-sm max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl font-black">Edit Manager</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {[["name", "Company / Name"], ["contact_name", "Primary Contact"], ["email", "Email"], ["phone", "Phone"], ["sms_phone", "Text/SMS number (E.164 e.g. +15125551234)"], ["mailing_address", "Mailing Address"], ["website", "Website"], ["software_url", "Management Software URL"], ["states_covered", "States Covered"]].map(([k, label]) => (
                <div key={k}>
                  <Label className="text-xs uppercase tracking-wider font-bold">{label}</Label>
                  <Input data-testid={`edit-pm-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-sm mt-1" />
                </div>
              ))}
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-xs uppercase tracking-wider font-bold">Fee %</Label><Input data-testid="edit-pm-management_fee_pct" type="number" value={form.management_fee_pct} onChange={(e) => setForm({ ...form, management_fee_pct: e.target.value })} className="rounded-sm mt-1" /></div>
                <div><Label className="text-xs uppercase tracking-wider font-bold">Start</Label><Input type="date" value={form.contract_start || ""} onChange={(e) => setForm({ ...form, contract_start: e.target.value })} className="rounded-sm mt-1" /></div>
                <div><Label className="text-xs uppercase tracking-wider font-bold">End</Label><Input type="date" value={form.contract_end || ""} onChange={(e) => setForm({ ...form, contract_end: e.target.value })} className="rounded-sm mt-1" /></div>
              </div>
              {/* Additional contacts */}
              <div>
                <div className="flex items-center justify-between"><Label className="text-xs uppercase tracking-wider font-bold">Additional Contacts</Label>
                  <button data-testid="add-contact-row" onClick={() => setForm({ ...form, additional_contacts: [...(form.additional_contacts || []), { name: "", role: "", email: "", phone: "" }] })} className="text-xs font-semibold flex items-center gap-1 hover:underline"><Plus size={13} /> Add</button>
                </div>
                {(form.additional_contacts || []).map((c, i) => (
                  <div key={i} className="grid grid-cols-2 gap-2 mt-2">
                    <Input placeholder="Name" value={c.name} onChange={(e) => { const a = [...form.additional_contacts]; a[i] = { ...a[i], name: e.target.value }; setForm({ ...form, additional_contacts: a }); }} className="rounded-sm h-9 text-sm" />
                    <Input placeholder="Role" value={c.role} onChange={(e) => { const a = [...form.additional_contacts]; a[i] = { ...a[i], role: e.target.value }; setForm({ ...form, additional_contacts: a }); }} className="rounded-sm h-9 text-sm" />
                    <Input placeholder="Email" value={c.email} onChange={(e) => { const a = [...form.additional_contacts]; a[i] = { ...a[i], email: e.target.value }; setForm({ ...form, additional_contacts: a }); }} className="rounded-sm h-9 text-sm" />
                    <div className="flex gap-1"><Input placeholder="Phone" value={c.phone} onChange={(e) => { const a = [...form.additional_contacts]; a[i] = { ...a[i], phone: e.target.value }; setForm({ ...form, additional_contacts: a }); }} className="rounded-sm h-9 text-sm" />
                      <button onClick={() => setForm({ ...form, additional_contacts: form.additional_contacts.filter((_, idx) => idx !== i) })} className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button></div>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter><Button data-testid="save-edit-pm-button" onClick={saveEdit} className="rounded-sm font-semibold w-full">Save Changes</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function ContractTermsPanel({ pm, pmId, onSaved, onUpload, onOpenContract, contractRef }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [form, setForm] = useState({
    management_fee_pct: pm.management_fee_pct ?? "",
    contract_start: pm.contract_start || "",
    contract_end: pm.contract_end || "",
    contract_terms_summary: pm.contract_terms_summary || "",
    contract_notes: pm.contract_notes || "",
  });

  useEffect(() => {
    setForm({
      management_fee_pct: pm.management_fee_pct ?? "",
      contract_start: pm.contract_start || "",
      contract_end: pm.contract_end || "",
      contract_terms_summary: pm.contract_terms_summary || "",
      contract_notes: pm.contract_notes || "",
    });
  }, [pm.id, pm.management_fee_pct, pm.contract_start, pm.contract_end, pm.contract_terms_summary, pm.contract_notes]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...pm,
        management_fee_pct: form.management_fee_pct === "" || form.management_fee_pct == null ? null : Number(form.management_fee_pct),
        contract_start: form.contract_start || null,
        contract_end: form.contract_end || null,
        contract_terms_summary: form.contract_terms_summary || "",
        contract_notes: form.contract_notes || "",
      };
      await api.put(`/property-managers/${pmId}`, payload);
      toast.success("Contract terms saved");
      setEditing(false);
      onSaved();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const parseWithAI = async () => {
    setParsing(true);
    try {
      const r = await api.post(`/property-managers/${pmId}/contract/parse`);
      const s = r.data?.suggestions || {};
      setForm((f) => ({
        ...f,
        management_fee_pct: s.management_fee_pct ?? f.management_fee_pct,
        contract_start: s.contract_start || f.contract_start,
        contract_end: s.contract_end || f.contract_end,
        contract_terms_summary: s.terms_summary || f.contract_terms_summary,
      }));
      setEditing(true);
      const trace = r.data?.parse_trace;
      toast.success(`Parsed via ${trace?.tier_used || "AI"}${trace?.doc_class ? ` · ${trace.doc_class}` : ""}. Review & Save.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI parse failed");
    } finally {
      setParsing(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-sm p-5" data-testid="contract-terms-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Management Contract</p>
        <div className="flex gap-2">
          {pm.contract_filename && !editing && (
            <Button data-testid="parse-contract-ai" size="sm" variant="outline" onClick={parseWithAI} disabled={parsing} className="rounded-sm">
              {parsing ? "Parsing…" : "Parse with AI"}
            </Button>
          )}
          {!editing && (
            <Button data-testid="edit-contract-terms" size="sm" variant="outline" onClick={() => setEditing(true)} className="rounded-sm">
              <PencilSimple size={14} className="mr-1" /> Edit terms
            </Button>
          )}
          <input ref={contractRef} type="file" accept="application/pdf" hidden onChange={onUpload} data-testid="contract-upload-input" />
          <Button data-testid="upload-contract-button" size="sm" variant="outline" onClick={() => contractRef.current?.click()} className="rounded-sm">
            <UploadSimple size={14} className="mr-1" /> {pm.contract_filename ? "Replace PDF" : "Upload PDF"}
          </Button>
        </div>
      </div>

      {!editing ? (
        <>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div><p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Fee</p><p data-testid="contract-fee-display" className="font-mono font-bold">{pm.management_fee_pct != null ? `${pm.management_fee_pct}%` : "—"}</p></div>
            <div><p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Start</p><p data-testid="contract-start-display" className="font-mono">{pm.contract_start || "—"}</p></div>
            <div><p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">End</p><p data-testid="contract-end-display" className="font-mono">{pm.contract_end || "—"}</p></div>
          </div>
          {(pm.contract_terms_summary || pm.contract_notes) && (
            <div className="mt-4 space-y-3">
              {pm.contract_terms_summary && (
                <div>
                  <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-1">Terms summary</p>
                  <p data-testid="contract-terms-summary-display" className="text-sm whitespace-pre-wrap">{pm.contract_terms_summary}</p>
                </div>
              )}
              {pm.contract_notes && (
                <div>
                  <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-1">Internal notes</p>
                  <p data-testid="contract-notes-display" className="text-sm whitespace-pre-wrap text-muted-foreground">{pm.contract_notes}</p>
                </div>
              )}
            </div>
          )}
          {pm.contract_filename && (
            <button data-testid="view-contract-button" onClick={onOpenContract} className="mt-3 flex items-center gap-2 text-sm font-semibold hover:underline">
              <FilePdf size={16} weight="fill" /> {pm.contract_filename}
            </button>
          )}
        </>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Fee %</Label>
              <Input data-testid="contract-fee-input" type="number" step="0.01" value={form.management_fee_pct} onChange={(e) => setForm({ ...form, management_fee_pct: e.target.value })} className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Start</Label>
              <Input data-testid="contract-start-input" type="date" value={form.contract_start} onChange={(e) => setForm({ ...form, contract_start: e.target.value })} className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">End</Label>
              <Input data-testid="contract-end-input" type="date" value={form.contract_end} onChange={(e) => setForm({ ...form, contract_end: e.target.value })} className="rounded-sm mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold">Terms summary</Label>
            <Textarea data-testid="contract-terms-summary-input" value={form.contract_terms_summary} onChange={(e) => setForm({ ...form, contract_terms_summary: e.target.value })} placeholder="Renewal, notice period, fee schedule, what's included/excluded, eviction handling, etc." className="rounded-sm mt-1 min-h-[100px]" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold">Internal notes</Label>
            <Textarea data-testid="contract-notes-input" value={form.contract_notes} onChange={(e) => setForm({ ...form, contract_notes: e.target.value })} placeholder="Private notes (not on the contract): e.g. fee was negotiated down from 10%, contact prefers SMS, etc." className="rounded-sm mt-1 min-h-[70px]" />
          </div>
          <div className="flex gap-2">
            <Button data-testid="save-contract-terms" onClick={save} disabled={saving} className="rounded-sm font-semibold">{saving ? "Saving…" : "Save"}</Button>
            <Button data-testid="cancel-contract-terms" variant="outline" onClick={() => { setEditing(false); setForm({ management_fee_pct: pm.management_fee_pct ?? "", contract_start: pm.contract_start || "", contract_end: pm.contract_end || "", contract_terms_summary: pm.contract_terms_summary || "", contract_notes: pm.contract_notes || "" }); }} className="rounded-sm">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
